/**
 * Browser-side configuration for "Sign in with Dhruvanta" (KAN-2036, epic KAN-2030).
 *
 * This is the ONLY place the SPA decides how Dhruvanta identity is reached. It is
 * deliberately tiny and contains NO protocol code: the whole OIDC ceremony now runs
 * SERVER-SIDE in the same-origin BFF broker (`api/idp/[...path].ts`), and the shared
 * `<DhruvantaAuth config={{ posture: 'bff' }}>` card from `@dhruvanta/ui/react` calls
 * the standard `/idp/bff/*` endpoints itself.
 *
 * CROWN-JEWEL INVARIANT — nothing here (or anywhere downstream of it) reads or
 * writes an IdP token. The browser holds only httpOnly cookies it cannot read:
 * the sealed BFF companion (`RETAIL_<PORTAL>_BFF`) and the retail session cookie
 * the auth-service seats through the bridge. `tests/tokenFreeInvariant.test.ts`
 * fails the build if that regresses.
 */

import type { PortalKey } from '../types'

/**
 * How retail reaches Dhruvanta identity.
 *
 *  - `'bff'`    (DEFAULT) — the token-free same-origin broker. The primary path.
 *  - `'legacy'` — the pre-KAN-2036 browser-side OIDC client (KAN-668 / KAN-747).
 *                 TODO(deprecation): see `src/services/dhruvantaSso.ts`. Kept only so
 *                 the cutover is reversible WITHOUT a code change; it puts live IdP
 *                 tokens in the JS heap and must not be used for new work.
 *  - `'off'`    — no Dhruvanta surface at all; retail-native email/password + OTP only.
 *
 * The retail-native login is ALWAYS available in every mode — this feature is purely
 * additive, so a misconfigured or unreachable IdP can never lock anyone out.
 */
export type DhruvantaAuthMode = 'bff' | 'legacy' | 'off'

const RAW_MODES: readonly DhruvantaAuthMode[] = ['bff', 'legacy', 'off']

/**
 * The five retail role surfaces, as broker seat variants. This list MUST agree with
 * `RETAIL_PORTALS` in `api/idp/brokerConfig.ts` (the server half) — a drift guard in
 * `tests/dhruvantaAuthConfig.test.ts` asserts it.
 *
 * The variant is opaque to the browser: it is forwarded on every login leg and the
 * BROKER decides which retail bridge to call and which session cookie is seated. The
 * browser cannot escalate by lying about it — the server-side sealed container and
 * the retail auth-service both re-check entitlement.
 */
export const DHRUVANTA_AUTH_VARIANTS: Record<PortalKey, string> = {
  customer: 'customer',
  seller: 'seller',
  delivery: 'delivery',
  operations: 'operations',
  admin: 'admin',
}

/** The same-origin broker mount. Same string the Vercel rewrite in vercel.json serves. */
export const DHRUVANTA_BFF_BASE_PATH = '/idp/bff'

const env = import.meta.env as unknown as Record<string, string | undefined>

/**
 * Resolve the active mode. Defaults to `'bff'` — the secure path is the DEFAULT, and
 * an operator has to opt OUT explicitly. An unrecognised value also resolves to
 * `'bff'` rather than silently disabling sign-in.
 */
export function resolveDhruvantaAuthMode(): DhruvantaAuthMode {
  const raw = env.VITE_DHRUVANTA_AUTH_MODE?.trim().toLowerCase()
  if (raw && (RAW_MODES as readonly string[]).includes(raw)) {
    return raw as DhruvantaAuthMode
  }
  // Back-compat with the KAN-668 flag: an explicit VITE_DHRUVANTA_SSO_ENABLED=false
  // still turns the whole surface off.
  if (env.VITE_DHRUVANTA_SSO_ENABLED?.trim().toLowerCase() === 'false') return 'off'
  return 'bff'
}

/** True when the token-free BFF surface should render. */
export function isDhruvantaBffEnabled(): boolean {
  return resolveDhruvantaAuthMode() === 'bff'
}

/**
 * True when the pre-KAN-2036 browser-side flow should render.
 * TODO(deprecation): remove with `src/services/dhruvantaSso.ts`.
 */
export function isDhruvantaLegacyEnabled(): boolean {
  return resolveDhruvantaAuthMode() === 'legacy'
}

/** The broker seat variant for a portal. Falls back to the least-privileged surface. */
export function variantForPortal(portal?: PortalKey | null): string {
  if (portal && portal in DHRUVANTA_AUTH_VARIANTS) {
    return DHRUVANTA_AUTH_VARIANTS[portal]
  }
  return DHRUVANTA_AUTH_VARIANTS.customer
}

/**
 * Read the portal this bundle was built for from `document.body.dataset.portal`
 * (set by each `apps/<portal>/index.html`). Used when a sign-in surface is rendered
 * outside an `AuthProvider` that already knows its portal.
 */
export function portalFromDom(): PortalKey | undefined {
  if (typeof document === 'undefined') return undefined
  const value = document.body?.dataset?.portal
  return value && value in DHRUVANTA_AUTH_VARIANTS ? (value as PortalKey) : undefined
}

/** Per-portal card copy. Keeps each role surface honest about who it is for. */
export const DHRUVANTA_CARD_COPY: Record<PortalKey, { eyebrow: string; subtitle: string }> = {
  customer: {
    eyebrow: 'Sign in',
    subtitle: 'Use your Dhruvanta account to shop, track orders and manage returns.',
  },
  seller: {
    eyebrow: 'Seller sign in',
    subtitle: 'Use your Dhruvanta account to manage your catalogue, orders and settlements.',
  },
  delivery: {
    eyebrow: 'Delivery partner sign in',
    subtitle: 'Use your Dhruvanta account to pick up assignments and update deliveries.',
  },
  operations: {
    eyebrow: 'Operations sign in',
    subtitle: 'Use your Dhruvanta account to work the operations boards.',
  },
  admin: {
    eyebrow: 'Administrator sign in',
    subtitle: 'Use your Dhruvanta account to administer Dhruvanta Retail.',
  },
}
